import React, { useState, useRef } from 'react';
import Footer from './Footer';

// ──────────────────────────────────────────────
// LinkKeyPage.jsx — DocuAI 연동 페이지
// 업체코드 + 이메일 입력 → OTP 인증 → 연동키 생성
// 레거시 linkKey/page.tsx 포팅
// ──────────────────────────────────────────────

const LinkKeyPage = ({ API_BASE_URL, onNavigate }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('link'); // 'link' | 'auth'
    const [groupId, setGroupId] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userEmailValid, setUserEmailValid] = useState(false);
    const [authNum, setAuthNum] = useState('');
    const emailRef = useRef(null);

    const handleEmailInput = (v) => {
        setUserEmailValid(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v));
        setUserEmail(v);
    };

    const sendOtp = async () => {
        if (!userEmailValid) { alert('올바른 이메일 형식이 아닙니다'); return; }
        if (!groupId.trim()) { alert('업체코드를 입력해주세요.'); return; }
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail, type: 'DocuAI연동' }),
            });
            const data = await res.json();
            if (data.result || data.success) {
                setStep('auth');
            } else {
                alert(data.message || '인증번호 전송에 실패했습니다.');
            }
        } catch (e) {
            alert('인증 전송 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const requestLinkKey = async () => {
        if (!authNum || authNum.length < 6) { alert('6자리 인증번호를 입력해주세요.'); return; }
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/auth/link-key`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail, authNum, groupId }),
            });
            const data = await res.json();
            if (data.result || data.success) {
                alert('등록이 완료되었습니다.');
                // 부모 창으로 메시지 전달 (LCS 연동)
                if (window.opener) {
                    window.opener.postMessage(
                        { type: 'linkKey', payload: data.data },
                        'https://lcsv2.tlogin.net'
                    );
                }
            } else {
                alert(data.message || '연동 실패');
            }
        } catch (e) {
            alert('연동 처리 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={S.page}>
            {loading && <div style={S.loadingOverlay}><div style={S.spinner} /></div>}

            <div style={S.card}>
                {/* Logo */}
                <div style={S.logoWrap}>
                    <div style={S.logoIcon}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                    </div>
                    <span style={S.logoText}>DocuAI 연동</span>
                </div>

                {step === 'link' ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <h1 style={S.title}>DocuAI 연동</h1>
                        <p style={S.subtitle}>업체코드와 이메일을 입력해주세요</p>

                        <div style={{ marginBottom: '10px' }}>
                            <input
                                value={groupId}
                                onChange={(e) => setGroupId(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') emailRef.current?.focus(); }}
                                placeholder="업체코드를 입력해주세요."
                                style={S.input}
                                disabled={loading}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <input
                                ref={emailRef}
                                value={userEmail}
                                onChange={(e) => handleEmailInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') sendOtp(); }}
                                placeholder="이메일을 입력해주세요."
                                style={S.input}
                                disabled={loading}
                            />
                            {userEmail && !userEmailValid && (
                                <span style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', display: 'block' }}>
                                    이메일 형식이 올바르지 않습니다
                                </span>
                            )}
                        </div>
                        <button onClick={sendOtp} disabled={loading} style={S.primaryBtn}>인증</button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <h1 style={{ ...S.title, fontSize: '24px' }}>인증</h1>
                        <p style={S.subtitle}>인증 코드를 입력해주세요</p>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <input
                                    key={i}
                                    type="text"
                                    maxLength={1}
                                    value={authNum[i] || ''}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        if (!val && e.nativeEvent.inputType === 'deleteContentBackward') {
                                            setAuthNum(authNum.slice(0, i) + authNum.slice(i + 1));
                                            if (i > 0) e.target.previousSibling?.focus();
                                            return;
                                        }
                                        const newAuth = authNum.slice(0, i) + val + authNum.slice(i + 1);
                                        setAuthNum(newAuth.slice(0, 6));
                                        if (val && i < 5) e.target.nextSibling?.focus();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace' && !authNum[i] && i > 0) e.target.previousSibling?.focus();
                                        if (e.key === 'Enter') requestLinkKey();
                                    }}
                                    style={S.otpInput}
                                />
                            ))}
                        </div>
                        <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>
                            전송된 6자리 코드를 입력하세요.
                        </p>

                        <button onClick={requestLinkKey} disabled={loading} style={{ ...S.primaryBtn, marginBottom: '8px' }}>확인</button>
                        <button onClick={() => setStep('link')} style={S.outlineBtn}>뒤로가기</button>
                    </div>
                )}

                {/* Back to login link */}
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button onClick={() => onNavigate && onNavigate('login')} style={S.linkBtn}>← 로그인으로 돌아가기</button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

const S = {
    page: {
        minHeight: '100vh', width: '100vw',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        fontFamily: "'Pretendard Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    card: {
        minWidth: '420px', maxWidth: '450px', padding: '36px',
        borderRadius: '16px', background: '#fff',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
    },
    logoWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' },
    logoIcon: { width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, #059669, #0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    logoText: { fontSize: '22px', fontWeight: '800', background: 'linear-gradient(135deg, #059669, #0d9488)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    title: { fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '6px', color: '#1a1b1e' },
    subtitle: { fontSize: '14px', color: '#6b7280', textAlign: 'center', marginBottom: '20px' },
    input: { width: '100%', padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box' },
    primaryBtn: { width: '100%', padding: '13px', border: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #059669, #0d9488)', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
    outlineBtn: { width: '100%', padding: '13px', borderRadius: '10px', background: '#fff', border: '1px solid #e5e7eb', color: '#374151', fontSize: '15px', fontWeight: '500', cursor: 'pointer' },
    otpInput: { width: '46px', height: '52px', textAlign: 'center', fontSize: '22px', fontWeight: '600', border: '2px solid #e5e7eb', borderRadius: '10px', outline: 'none' },
    linkBtn: { background: 'none', border: 'none', color: '#059669', fontWeight: '600', cursor: 'pointer', fontSize: '14px' },
    loadingOverlay: { position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    spinner: { width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};

export default LinkKeyPage;
